import type { AnswerKeyEntry } from '@/modules/diagnostico/domain/entities/answer-key.entity';
import {
  toAnswerIndex,
  toAnswerOption,
  type Answers,
} from '@/modules/simulado/domain/entities/answer.entity';

/** As respostas do gabarito no formato do envio: número → índice. */
export function toGabaritoPayload(answers: Answers): Record<string, number> {
  return Object.fromEntries(
    Object.entries(answers).map(([number, letter]) => [
      number,
      toAnswerIndex(letter),
    ]),
  );
}

/** O caminho de volta: o envio lido do servidor vira o mapa da tela. */
export function fromGabaritoPayload(payload: Record<string, number>): Answers {
  const answers: Answers = {};
  for (const [number, index] of Object.entries(payload)) {
    const letter = toAnswerOption(index);
    if (letter) answers[Number(number)] = letter;
  }
  return answers;
}

export type QuestionOutcome =
  | 'right'
  | 'wrong'
  | 'blank'
  | 'annulled'
  | 'pending';

/**
 * O resultado de uma questão do gabarito contra o oficial. `pending` é a
 * questão cuja resposta oficial ainda não saiu: não é erro nem acerto.
 */
export function outcomeOf(
  entry: AnswerKeyEntry,
  answers: Answers,
): QuestionOutcome {
  if (entry.isExcluded) return 'annulled';
  if (entry.answer === null) return 'pending';
  const marked = answers[entry.number];
  if (!marked) return 'blank';
  return marked === entry.answer ? 'right' : 'wrong';
}

export interface GabaritoScore {
  /** A nota bruta: quantas a pessoa acertou. */
  acertos: number;
  erros: number;
  emBranco: number;
  /** Questões que valem: publicadas e não anuladas. É o denominador da nota. */
  validas: number;
  anuladas: number;
  /** Ainda sem resposta oficial. */
  pendentes: number;
}

/**
 * A correção do gabarito — **nota bruta**, que é o que ordena o ranking:
 * acerto vale um, erro e branco valem zero, e anulada sai da conta (não soma
 * nem pesa no denominador).
 */
export function scoreGabarito(
  answerKey: AnswerKeyEntry[],
  answers: Answers,
): GabaritoScore {
  const score: GabaritoScore = {
    acertos: 0,
    erros: 0,
    emBranco: 0,
    validas: 0,
    anuladas: 0,
    pendentes: 0,
  };

  for (const entry of answerKey) {
    const outcome = outcomeOf(entry, answers);
    if (outcome === 'annulled') score.anuladas += 1;
    else if (outcome === 'pending') score.pendentes += 1;
    else {
      score.validas += 1;
      if (outcome === 'right') score.acertos += 1;
      if (outcome === 'wrong') score.erros += 1;
      if (outcome === 'blank') score.emBranco += 1;
    }
  }

  return score;
}
